import {
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    EventEmitter,
    Input,
    TemplateRef,
    ViewEncapsulation,
    Output,
} from '@angular/core';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';

export type ItemStatusTypeFn<T> = (item: T, index: number) => boolean;
export type GetTimerDataFn<T> = (item: T, index: number) => any;

type ItemStatus = 'completed' | 'active' | 'in-progress' | 'pending';

@Component({
    selector: 'fi-progress-timeline',
    templateUrl: './fi-progress-timeline.component.html',
    styleUrl: './fi-progress-timeline.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-progress-timeline' },
    standalone: false,
})
export class FiProgressTimelineComponent<T = unknown> {
    @Input({ required: true }) items: T[] = [];
    @Input() isCompletedFn: ItemStatusTypeFn<T> = () => false;
    @Input() isActiveFn: ItemStatusTypeFn<T> = () => false;
    @Input() isDisabledFn: ItemStatusTypeFn<T> = () => false;
    @Input() isInProgressFn: ItemStatusTypeFn<T> = () => false;
    @Input() getTimerDataFn: ItemStatusTypeFn<T> = () => undefined;

    @Output() timerFinished = new EventEmitter<T>();

    protected isNullOrUndefined = isNullOrUndefined;

    @ContentChild(TemplateRef) contentTemplate?: TemplateRef<{ $implicit: T; index: number; last: boolean }>;

    protected getItemStatus(item: T, index: number): ItemStatus {
        if (this.isCompletedFn(item, index)) {
            return 'completed';
        }
        if (this.isInProgressFn(item, index)) {
            return 'in-progress';
        }
        if (this.isActiveFn(item, index)) {
            return 'active';
        }
        return 'pending';
    }

    protected getTimerData(item: T, index: number): any {
        return this.getTimerDataFn(item, index);
    }

    protected isLastItem(index: number): boolean {
        return index === this.items.length - 1;
    }

    protected shouldShowConnector(index: number): boolean {
        return !this.isLastItem(index);
    }

    protected getConnectorStatus(item: T, index: number): ItemStatus {
        const currentStatus = this.getItemStatus(item, index);
        return currentStatus === 'completed' ? 'completed' : 'pending';
    }
}
